import type { FigurePoint } from './arabicSky';

// Constellation styles. Modern (the IAU's 88) and Arabian (star names and the Moon's stations) have
// their own code. A style loaded from a file (public/cultures/) has none at present: the H. A. Rey shapes were
// removed (the owner, 9 October 2026), and a saved choice of them loads as Modern.
// Ahillah keeps to factual sky content, so other peoples' myths and sky lore are not included.

export interface CultureFigure {
  en: string;
  native: string;
  pron: string;
  lines: FigurePoint[][];
  text?: string[];
}

export interface Culture {
  id: string;
  name: string;
  short: string;
  licence: string;
  licenceUrl: string;
  /** NoDerivatives: the text is shown exactly as published. */
  nd: boolean;
  /** Show the native-script name on the sky (not for scripts most phones cannot draw). */
  showNative: boolean;
  intro: string[];
  authors: string;
  source: string;
  sourceUrl?: string;
  adapted: string;
  figures: CultureFigure[];
  stars: Record<string, { en?: string; native?: string; pron?: string }>;
}

export type CultureId = 'modern' | 'arabic';

export const CULTURES: { id: CultureId; name: string; short: string }[] = [
  { id: 'modern', name: 'Modern (IAU)', short: 'The 88 constellations astronomers use today' },
  { id: 'arabic', name: 'Arabian star names', short: 'The Arabic names of the stars and star groups, and the Moon’s 28 stations' },
];

/** Styles loaded from a file (everything except Modern and Arabian). */
export const isSharedCulture = (id: string) => id !== 'modern' && id !== 'arabic';

const cache = new Map<string, Promise<Culture>>();
export function loadCulture(id: string): Promise<Culture> {
  let p = cache.get(id);
  if (!p) {
    p = fetch(`${import.meta.env.BASE_URL}cultures/${id}.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`culture ${id}: ${r.status}`);
        return r.json() as Promise<Culture>;
      })
      .catch((e) => {
        cache.delete(id);
        throw e;
      });
    cache.set(id, p);
  }
  return p;
}
